import React from "react";
import ApiService from "../../services/api";
import { convertViewName } from "../../utils/viewUtils";

/**
 * Custom hook for tracking session view changes and updating session time spent
 * 
 * This hook:
 * - Tracks time spent on each view
 * - Updates the session when the view changes
 * - Handles cleanup on unmount
 * - Prevents race conditions with mounted refs and timeouts
 * 
 * @param currentView - The current view name
 * @param sessionId - The session ID (optional)
 */
export const useSessionTracking = (
  currentView: string,
  sessionId: string | null
): void => {
  const viewStartTimeRef = React.useRef<number>(Date.now());
  const previousViewRef = React.useRef<string>(currentView);
  const mountedRef = React.useRef(true);
  const cleanupTimeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

  React.useEffect(() => {
    const apiService = new ApiService(sessionId);
    let timeoutId: NodeJS.Timeout | undefined;
    
    if (previousViewRef.current !== currentView) {
      const timeSpentSeconds = Math.floor(
        (Date.now() - viewStartTimeRef.current) / 1000
      );

      if (sessionId && mountedRef.current) {
        // Add a small delay to prevent concurrent calls
        timeoutId = setTimeout(() => {
          if (mountedRef.current) {
            apiService
              .updateSession(
                convertViewName(previousViewRef.current),
                timeSpentSeconds
              )
              .catch((error: Error) =>
                console.error("Failed to update screen time:", error)
              );
          }
        }, 100); // 100ms delay
      }

      viewStartTimeRef.current = Date.now();
      previousViewRef.current = currentView;
    }

    return () => {
      // Clear any pending timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      const timeSpentSeconds = Math.floor(
        (Date.now() - viewStartTimeRef.current) / 1000
      );
      if (sessionId && mountedRef.current) {
        // Add a small delay for cleanup calls too
        cleanupTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            apiService
              .updateSession(convertViewName(currentView), timeSpentSeconds)
              .catch((error: Error) =>
                console.error("Failed to update screen time:", error)
              );
          }
        }, 150); // Slightly longer delay for cleanup
      }
    };
  }, [currentView, sessionId]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, []);
};
